<template>
  <div class="mev">
    <native-input
      v-if="[0, 1, 2].includes(parameterValueType)"
      class="mev__input"
      :show-border="true"
      :edit-on-click="true"
      :clear-hotkey="true"
      :disabled-field="disable"
      :model-value="theModel.stringValue"
      :external-warning="!!numberWarning"
      :external-warning-text="numberWarning"
      @liveValue="(val) => { liveText = val; }"
      @update:modelValue="(val) => { theModel.stringValue = val; }"
    />

    <mosta-tree-select
      v-else-if="parameterValueType === 4"
      ref="vsetSelect"
      class="mev__vset"
      :display-text="valueSetDisplayText"
      :placeholder="vsetTitle || $t('values_set')"
      :disable="disable"
      :show-free-input="canCreateValue"
      :free-input-tooltip="$t('add_value')"
      @update:search="(val) => { searchText = val; }"
      @freeInputPressed="createValueFromSearch"
      @keyEnter="onValueSetEnter"
      @open="scheduleTruncationCheck"
    >
      <loading-skeleton v-if="vsetLoading" />
      <!-- Подтип дата: новое значение набора выбирается календарём, ручной ввод тут не подходит -->
      <div v-if="!vsetLoading && parameterSetValueType === 3" class="mev__vset-date med-date">
        <VueDatePicker
          utc="preserve"
          timezone="Europe/Moscow"
          :locale="$i18n.locale"
          :teleport="true"
          :clearable="false"
          :enable-time-picker="false"
          :format="formatDate"
          :text-input="textInputOptions"
          :alt-position="positionDateMenu"
          :auto-apply="true"
          :action-row="{ showSelect: false, showCancel: false, showPreview: false }"
          :model-value="null"
          @update:model-value="createDateValue"
        />
      </div>
      <div
        v-for="opt in (vsetLoading ? [] : vsetFilteredOptions)"
        :key="opt.Id"
        :ref="(el) => setOptionRef(opt.Id, el)"
        class="mev__vset-item"
        :class="{ 'mev__vset-item--active': isValueSelected(opt) }"
        @mouseenter="checkTruncation(opt.Id)"
        @click="selectValue(opt)"
      >
        {{ opt.Name }}
        <q-tooltip
          :no-parent-event="!truncatedOptions[opt.Id]"
          :delay="300"
          anchor="top left"
          self="bottom left"
          :offset="[0, 0]"
          class="mev-tooltip"
        >
          {{ opt.Name }}
        </q-tooltip>
      </div>
      <div v-if="!vsetLoading && !vsetFilteredOptions.length" class="mev__vset-empty">
        {{ $t('nothing_found') }}
      </div>
    </mosta-tree-select>

    <div v-else-if="parameterValueType === 6" class="mev__date med-date">
      <VueDatePicker
        utc="preserve"
        timezone="Europe/Moscow"
        :locale="$i18n.locale"
        :teleport="true"
        :clearable="false"
        :disabled="disable"
        :enable-time-picker="false"
        :format="formatDate"
        :text-input="textInputOptions"
        :alt-position="positionDateMenu"
        :auto-apply="true"
        :action-row="{ showSelect: false, showCancel: false, showPreview: false }"
        v-model="dateModel"
      />
      <q-tooltip
        :no-parent-event="!!dateModel"
        :delay="300"
        anchor="top left"
        self="bottom left"
        :offset="[0, 0]"
        class="mev-tooltip"
      >
        {{ $t('field_cannot_be_empty') }}
      </q-tooltip>
    </div>

    <mosta-checkbox
      v-else-if="parameterValueType === 3"
      class="mev__bool"
      :disable="disable"
      v-model="theModel.checkBoxModel"
      :label="$t('yes_label') + ' / ' + $t('no_label')"
    />
  </div>
</template>

<script lang="ts">
import { date } from "quasar";
import { Component, Emit, Prop, Vue, Watch } from "vue-facing-decorator";
import NativeInput from "@/components/common/basic/NativeInput.vue";
import MostaCheckbox from "@/components/common/basic/MostaCheckbox.vue";
import MostaTreeSelect from "@/components/common/MostaTreeSelect.vue";
import LoadingSkeleton from "@/components/common/aux-selector/refactored/components/LoadingSkeleton.vue";
import { ParamsRow } from "./MultiEditLine.vue";
import {
  V2ParameterValueSetExtensionType,
  V2ParameterValueSet_ValueType,
} from "@/models/etc/parameter-types";

// Формат, в котором значения даты уходят на бэк. Модель строки остаётся строкой,
// календарь отдаёт Date, поэтому конвертация идёт здесь.
const DATE_WIRE_MASK = "YYYY-MM-DDTHH:mm:ssZ";

@Component({
  name: "MultiEditValueRow",
  components: { NativeInput, MostaCheckbox, MostaTreeSelect, LoadingSkeleton },
})
export default class MultiEditValueRow extends Vue {
  @Prop({ default: () => ({}) as ParamsRow })
  modelValue!: ParamsRow;
  @Emit("update:modelValue") emitModelValue(arg: ParamsRow) {
    return arg;
  }

  @Prop({ default: 0 })
  parameterValueType!: number;
  @Prop({ default: 0 })
  parameterSetValueType!: number;
  @Prop({ default: 0 })
  vsetType!: number;
  @Prop({ default: "" })
  vsetTitle?: string;
  @Prop({ default: () => [] })
  vsetOptions!: any[];
  @Prop({ default: false })
  vsetLoading!: boolean;
  @Prop({ default: false })
  disable!: boolean;

  liveText = "";
  searchText = "";
  // Подсказка с полным именем нужна только тем строкам, которые реально обрезаны по ширине
  truncatedOptions: Record<string, boolean> = {};
  private optionRefs: Record<string, HTMLElement | null> = {};

  get theModel(): ParamsRow {
    return this.modelValue;
  }

  get textInputOptions() {
    return {
      enterSubmit: true,
      tabSubmit: true,
      openMenu: "toggle",
      selectOnFocus: false,
      format: "dd.MM.yyyy",
    };
  }

  formatDate(value: Date): string {
    const day = value.getDate();
    const month = value.getMonth() + 1;
    return `${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}.${value.getFullYear()}`;
  }

  // Своё позиционирование панели: со штатным пикер сначала рисует её у левого края,
  // а координаты досчитывает вторым кадром, из-за чего календарь на глазах переезжает.
  positionDateMenu(inputEl: HTMLElement): Record<string, string> {
    const rect = inputEl.getBoundingClientRect();
    const gap = 4;
    // Высота панели до отрисовки неизвестна, для выбора стороны хватает оценки
    const approxHeight = 320;
    const flipUp =
      rect.bottom + gap + approxHeight > window.innerHeight && rect.top > approxHeight;

    if (flipUp) {
      return {
        position: "fixed",
        bottom: `${window.innerHeight - rect.top + gap}px`,
        left: `${rect.left}px`,
      };
    }
    return {
      position: "fixed",
      top: `${rect.bottom + gap}px`,
      left: `${rect.left}px`,
    };
  }

  get dateModel(): Date | null {
    return this.theModel.dateValueModel ? new Date("" + this.theModel.dateValueModel) : null;
  }

  set dateModel(val: Date | null) {
    this.theModel.dateValueModel = val ? date.formatDate(val, DATE_WIRE_MASK) : "";
  }

  isNumeric(str: string): boolean {
    return !isNaN(+str) && !isNaN(parseInt(str));
  }

  isNumericFloat(str: string): boolean {
    return !isNaN(+str) && !isNaN(parseFloat(str));
  }

  // Пустое поле не считается ошибкой: значение параметра можно и стереть
  get numberWarning(): string {
    const val = (this.liveText || "").trim();
    if (!val) return "";
    if (this.parameterValueType === 1 && !this.isNumeric(val)) {
      return this.$t("enter_integer");
    }
    if (this.parameterValueType === 2 && !this.isNumericFloat(val)) {
      return this.$t("input_float");
    }
    return "";
  }

  // Значение набора хранится одним объектом, пустое состояние - пустой массив
  get valueSetDisplayText(): string {
    const val = this.theModel.vsetValues;
    if (!val || Array.isArray(val)) return "";
    return "" + (val.Name ?? "");
  }

  isValueSelected(opt: any): boolean {
    const val = this.theModel.vsetValues;
    if (!val || Array.isArray(val)) return false;
    return val.Id === opt.Id;
  }

  setOptionRef(id: any, el: any) {
    this.optionRefs["" + id] = (el as HTMLElement) || null;
  }

  checkTruncation(id: any) {
    const key = "" + id;
    const el = this.optionRefs[key];
    if (!el) return;
    this.truncatedOptions[key] = el.scrollWidth > el.clientWidth + 1;
  }

  // Ширину дропдауна floating-ui подставляет уже после первого кадра, поэтому меряем
  // строки не сразу, а следующим тиком таймера
  scheduleTruncationCheck() {
    this.$nextTick(() => {
      setTimeout(() => {
        Object.keys(this.optionRefs).forEach((key) => this.checkTruncation(key));
      }, 0);
    });
  }

  @Watch("searchText")
  onSearchChanged() {
    this.scheduleTruncationCheck();
  }

  @Watch("vsetLoading")
  onVsetLoadingChanged() {
    this.optionRefs = {};
    this.truncatedOptions = {};
    this.scheduleTruncationCheck();
  }

  get vsetFilteredOptions(): any[] {
    const needle = (this.searchText || "").toLowerCase();
    if (!needle) return this.vsetOptions;
    return this.vsetOptions.filter(
      (v) => ("" + v.Name).toLowerCase().indexOf(needle) > -1
    );
  }

  // Новое значение можно завести только в расширяемом наборе. Для подтипа дата
  // значение приходит из календаря, а не из строки поиска.
  get canCreateValue(): boolean {
    const extendable =
      this.vsetType === V2ParameterValueSetExtensionType.ExtendableOnApply ||
      this.vsetType === V2ParameterValueSetExtensionType.Extendable;
    return (
      !!this.searchText.length &&
      this.parameterSetValueType !== V2ParameterValueSet_ValueType.Date &&
      extendable
    );
  }

  selectValue(opt: any) {
    this.theModel.vsetValues = opt;
    (this.$refs.vsetSelect as any)?.close?.();
  }

  onValueSetEnter() {
    if (this.canCreateValue) {
      this.createValueFromSearch();
      return;
    }
    if (this.vsetFilteredOptions.length) {
      this.selectValue(this.vsetFilteredOptions[0]);
    }
  }

  createValueFromSearch() {
    const val = (this.searchText || "").trim();
    if (!val) return;

    const found = this.vsetOptions.find((el) => ("" + el.Name).trim() === val);
    if (found) {
      this.selectValue(found);
      return;
    }

    if (this.parameterSetValueType === V2ParameterValueSet_ValueType.String) {
      this.selectValue({ Id: "0_" + val, Name: val, NameString: val, new: true });
      return;
    }

    const numberFits =
      (this.parameterSetValueType === V2ParameterValueSet_ValueType.Int && this.isNumeric(val)) ||
      (this.parameterSetValueType === V2ParameterValueSet_ValueType.Decimal && this.isNumericFloat(val));
    if (numberFits) {
      this.selectValue({
        Id: "0_" + val,
        Name: parseFloat(val),
        NameNumber: parseFloat(val),
        new: true,
      });
    }
  }

  createDateValue(val: Date | null) {
    if (!val) return;
    const wire = date.formatDate(val, DATE_WIRE_MASK);
    const found = this.vsetOptions.find((el) => ("" + el.Name).trim() === wire);
    if (found) {
      this.selectValue(found);
      return;
    }
    this.selectValue({
      Id: "0_" + wire,
      Name: date.formatDate(val, "DD.MM.YYYY"),
      NameDate: wire,
      new: true,
    });
  }
}
</script>

<style lang="scss" scoped>
.mev {
  display: flex;
  flex: 1;
  min-width: 0;
}

.mev__input {
  width: 100%;
  min-height: 32px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #D2D2D2;
  background: #FFF;
  color: #4D4D4D;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
}

.mev__vset,
.mev__date {
  width: 100%;
}

.mev__bool {
  margin: 6px 0;
}

.mev__vset-date {
  padding: 0 8px 4px;
}

.mev__vset-item {
  min-height: 28px;
  padding: 5px 12px 5px 8px;
  border-radius: 4px;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: var(--Text-color-text_main, #4D4D4D);
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mev__vset-item:hover {
  background: var(--color-divider, #F2F2F2);
}

.mev__vset-item--active {
  color: var(--color-accent, #3F884F);
}

.mev__vset-empty {
  padding: 8px 12px;
  color: #999;
  font-family: Roboto;
  font-size: 14px;
  line-height: 18px;
}
</style>

<style lang="scss">
.med-date {
  --dp-border-radius: 8px;
  --dp-font-family: Roboto, sans-serif;
  --dp-font-size: 14px;
  --dp-input-padding: 4px 12px;
  --dp-input-icon-padding: 34px;
}

/* Цветовые переменные объявлены в самом .dp__theme_light, а он висит на корне пикера,
   то есть ниже обёртки, поэтому переопределять их надо на том же элементе */
.med-date .dp__theme_light {
  --dp-border-color: #D2D2D2;
  --dp-border-color-hover: #3F884F;
  --dp-border-color-focus: #3F884F;
  --dp-text-color: #4D4D4D;
  --dp-icon-color: #979797;
}

.mev__input .native-input__clear-icon {
  font-size: 16px !important;
}

/* Бокс иконки очистки складывается в ноль, сама иконка рисуется поверх содержимого:
   её появление при постановке каретки не должно менять высоту поля */
.mev__input .native-input__clear-wrap {
  height: 0;
  line-height: 0;
}

.med-date .dp__input {
  min-height: 32px;
  width: 100%;
}

.med-date .dp__input_icons {
  padding: 6px 6px 6px 10px;
}

.med-date:hover .dp__theme_light {
  --dp-icon-color: #3F884F;
}

.q-tooltip.mev-tooltip {
  z-index: 10010;
  max-width: 280px;
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
